/**
 * Solicitudes de incauto por lote.
 *
 * Un lote = un incautador del catálogo + uno o varios préstamos en mora.
 * Al enviarlo se crea UNA autorización para el AdmG (bandeja del Inicio),
 * que aprueba o rechaza el lote completo y puede quitar préstamos al
 * aprobar. Los préstamos de un lote aprobado se incautan desde aquí sin
 * pedir otra autorización. Backend: solicitudesincautobackend.py.
 */
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import AssignmentLateOutlinedIcon from "@mui/icons-material/AssignmentLateOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useGlobalMDAlert } from "context";
import {
  FP,
  PageHeader,
  FpDataGrid,
  FiltroPildoras,
  StatusPill,
  BarraVolver,
  fpInputSx,
} from "components/financepro";
import { useApi } from "lib/api";
import * as XLSX from "xlsx";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import RegistrarIncauto from "modules/incautos/registrarincauto";
import { imprimirOrdenLote, imprimirActasIncauto } from "modules/incautos/impresionincauto";
import IconButton from "@mui/material/IconButton";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";

export const MOTIVOS = [
  "Mora",
  "Abandono del vehiculo",
  "Cliente no localizable",
  "Fraude",
  "Otro",
];

const fmtMoney = (v) =>
  (Number(v) || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PILL_LOTE = {
  Pendiente: "info",
  Aprobado: "warning",
  Cerrado: "success",
  Rechazado: "danger",
  Cancelado: "neutral",
};
const PILL_LINEA = {
  Pendiente: "info",
  "Por ejecutar": "warning",
  Ejecutada: "success",
  Rechazada: "danger",
  Cancelada: "neutral",
};

const FILTROS = [
  { id: "activas", label: "En curso", estados: ["Pendiente", "Aprobado"] },
  { id: "Pendiente", label: "Por aprobar", estados: ["Pendiente"], color: FP.color.info },
  { id: "Aprobado", label: "Aprobadas", estados: ["Aprobado"], color: FP.color.warning },
  { id: "Cerrado", label: "Cerradas", estados: ["Cerrado"], color: FP.color.success },
  {
    id: "otras",
    label: "Rechazadas / canceladas",
    estados: ["Rechazado", "Cancelado"],
    color: FP.color.danger,
  },
  { id: "todas", label: "Todas", estados: null },
];

const btn = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnAzul = {
  ...btn,
  bgcolor: FP.color.accentBlue,
  color: "#fff",
  "&:hover": { bgcolor: FP.color.navySoft, color: "#fff" },
  "&.Mui-disabled": { bgcolor: `${FP.color.accentBlue}66`, color: "#fff" },
};
const btnRojo = { ...btn, color: FP.color.danger };

function RazonDialog({ open, titulo, onClose, onConfirmar, procesando }) {
  const [razon, setRazon] = useState("");
  useEffect(() => {
    if (open) setRazon("");
  }, [open]);
  return (
    <Dialog open={open} onClose={() => !procesando && onClose()} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 800 }}>{titulo}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="Razón"
          value={razon}
          onChange={(e) => setRazon(e.target.value)}
          inputProps={{ maxLength: 250 }}
          sx={{ mt: 1, ...fpInputSx }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={procesando} sx={btn}>
          Volver
        </Button>
        <Button
          variant="contained"
          disabled={procesando || !razon.trim()}
          onClick={() => onConfirmar(razon.trim())}
          sx={{ ...btn, bgcolor: FP.color.danger, color: "#fff !important" }}
        >
          Cancelar solicitud
        </Button>
      </DialogActions>
    </Dialog>
  );
}
RazonDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  titulo: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirmar: PropTypes.func.isRequired,
  procesando: PropTypes.bool.isRequired,
};

/* ---------------------------------------------------------------------- */
/* Nuevo lote                                                              */
/* ---------------------------------------------------------------------- */

/** Uno o varios préstamos (número, texto o lista) → lista de textos sin repetir. */
function normalizarNoprests(v) {
  let lista = [];
  if (Array.isArray(v)) lista = v;
  else if (v !== null && v !== undefined && v !== "") lista = [v];
  return [...new Set(lista.map((x) => String(x).trim()).filter(Boolean))];
}

export function NuevoLote({ onExit, onCreado, noprestInicial }) {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();

  const [incautadores, setIncautadores] = useState([]);
  const [incautador, setIncautador] = useState(null);
  const [motivo, setMotivo] = useState("Mora");
  const [fechalimite, setFechalimite] = useState("");
  const [nota, setNota] = useState("");

  const [candidatos, setCandidatos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [buscar, setBuscar] = useState("");
  const [diasMin, setDiasMin] = useState("");
  const [cuotasMin, setCuotasMin] = useState("");
  const [frecuencia, setFrecuencia] = useState("Todas");
  const [seleccion, setSeleccion] = useState([]);
  const [enviando, setEnviando] = useState(false);

  const cargarCandidatos = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/solicitudincauto_candidatos", {});
      if (ok) {
        const lista = data.prestamos || [];
        setCandidatos(lista);
        // Préstamos que vienen marcados desde Morosidad, el expediente o
        // Gestión de cobros (uno o varios).
        const pedidos = normalizarNoprests(noprestInicial);
        if (pedidos.length) {
          const disponibles = new Set(lista.map((c) => c.noprest));
          const si = pedidos.filter((id) => disponibles.has(id));
          const no = pedidos.filter((id) => !disponibles.has(id));
          setSeleccion(si);
          if (no.length) {
            const motivo = "ya incautado, en otro lote, al día o de otra sucursal";
            const cuales = no.slice(0, 10).join(", ") + (no.length > 10 ? "…" : "");
            showAlert({
              message:
                pedidos.length === 1
                  ? `El préstamo ${no[0]} no se puede solicitar (${motivo})`
                  : `${no.length} de ${pedidos.length} préstamos no se pueden solicitar (${motivo}): ${cuales}`,
              color: "warning",
            });
          }
        }
      } else
        showAlert({ message: data.Error || "No se pudieron cargar los préstamos", color: "error" });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    post("/api/incautadores_listar", { soloactivos: true }).then(({ ok, data }) => {
      if (ok) setIncautadores(data.incautadores || []);
    });
    cargarCandidatos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibles = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    const min = parseInt(diasMin, 10) || 0;
    const minCuotas = parseInt(cuotasMin, 10) || 0;
    return candidatos.filter((c) => {
      // Lo ya marcado no se esconde al cambiar los filtros.
      if (seleccion.includes(c.noprest)) return true;
      if (c.dias < min) return false;
      if (c.cuotas < minCuotas) return false;
      if (frecuencia !== "Todas" && c.frecuencia !== frecuencia) return false;
      if (!q) return true;
      return [c.noprest, c.cliente, c.cedula, c.chasis, c.direccion, c.urb, c.sucursal]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [candidatos, buscar, diasMin, cuotasMin, frecuencia, seleccion]);

  // Formas de pago presentes, en orden fijo.
  const frecuencias = useMemo(() => {
    const orden = ["Mensual", "Quincenal", "Semanal", "Diario"];
    const hay = Array.from(new Set(candidatos.map((c) => c.frecuencia).filter(Boolean)));
    return hay.sort((a, b) => {
      const ia = orden.indexOf(a) === -1 ? 99 : orden.indexOf(a);
      const ib = orden.indexOf(b) === -1 ? 99 : orden.indexOf(b);
      return ia - ib || a.localeCompare(b);
    });
  }, [candidatos]);

  const limpiarFiltros = () => {
    setBuscar("");
    setDiasMin("");
    setCuotasMin("");
    setFrecuencia("Todas");
  };

  const elegidos = candidatos.filter((c) => seleccion.includes(c.noprest));
  const totalVencido = elegidos.reduce((s, c) => s + (Number(c.vencido) || 0), 0);

  const enviar = async () => {
    if (!incautador) {
      showAlert({ message: "Seleccione el incautador", color: "warning" });
      return;
    }
    if (!seleccion.length) {
      showAlert({ message: "Marque al menos un préstamo", color: "warning" });
      return;
    }
    setEnviando(true);
    try {
      const { ok, data } = await post("/api/solicitudincauto_crear", {
        cedincautador: incautador.cedula,
        motivo,
        fechalimite,
        nota,
        noprests: seleccion,
      });
      if (ok) {
        showAlert({
          message: `Lote #${data.lote} enviado al AdmG para aprobación`,
          color: "success",
        });
        onCreado();
      } else {
        showAlert({ message: data.Error || "No se pudo crear la solicitud", color: "error" });
        if (Array.isArray(data.excluidos)) {
          setSeleccion((s) => s.filter((x) => !data.excluidos.includes(x)));
          cargarCandidatos();
        }
      }
    } finally {
      setEnviando(false);
    }
  };

  const columnas = [
    { field: "noprest", headerName: "Préstamo", width: 95 },
    { field: "cliente", headerName: "Cliente", flex: 1.2, minWidth: 170 },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      flex: 1,
      minWidth: 160,
      valueGetter: (_, r) => [r.marca, r.modelo, r.colore].filter(Boolean).join(" "),
    },
    { field: "chasis", headerName: "Chasis", width: 150 },
    {
      field: "ubicacion",
      headerName: "Dirección",
      flex: 1.2,
      minWidth: 170,
      valueGetter: (_, r) => [r.direccion, r.urb].filter(Boolean).join(", "),
    },
    { field: "sucursal", headerName: "Suc.", width: 70 },
    { field: "frecuencia", headerName: "Forma de pago", width: 115 },
    { field: "cuotas", headerName: "Cuotas atrasadas", type: "number", width: 95 },
    { field: "dias", headerName: "Días", type: "number", width: 70 },
    {
      field: "vencido",
      headerName: "Vencido",
      type: "number",
      width: 115,
      valueFormatter: (v) => fmtMoney(v),
    },
    {
      field: "preincautoescritorio",
      headerName: "",
      width: 40,
      sortable: false,
      renderCell: ({ row }) =>
        row.preincautoescritorio ? (
          <Tooltip title="Tiene un pre-incauto del escritorio: este lote lo reemplaza">
            <WarningAmberRoundedIcon sx={{ fontSize: 18, color: FP.color.warning }} />
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <BarraVolver onExit={onExit} />
      <PageHeader
        title="Nueva solicitud de incauto"
        subtitle="Un incautador, uno o varios préstamos y una sola aprobación del AdmG"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box
          sx={{
            p: 2.4,
            mb: 2,
            borderRadius: 2.5,
            bgcolor: FP.color.cardBg,
            border: `1px solid ${FP.color.border}`,
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={incautadores}
                value={incautador}
                onChange={(_, v) => setIncautador(v)}
                getOptionLabel={(o) => `${o.nombres || ""} — ${o.cedula || ""}`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                noOptionsText="No hay incautadores activos. Agréguelos en Incautos y Cargos → Incautadores"
                renderInput={(params) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <TextField {...params} label="Incautador" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Motivo"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                sx={fpInputSx}
              >
                {MOTIVOS.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Fecha límite"
                value={fechalimite}
                onChange={(e) => setFechalimite(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Nota (opcional)"
                value={nota}
                onChange={(e) => setNota(e.target.value)}
                inputProps={{ maxLength: 250 }}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
          sx={{ mb: 1.5 }}
        >
          <TextField
            size="small"
            placeholder="Buscar préstamo, cliente, chasis, dirección…"
            value={buscar}
            onChange={(e) => setBuscar(e.target.value)}
            sx={{ ...fpInputSx, flex: 1, minWidth: { sm: 220 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            size="small"
            label="Forma de pago"
            value={frecuencia}
            onChange={(e) => setFrecuencia(e.target.value)}
            sx={{ ...fpInputSx, width: { sm: 160 } }}
          >
            <MenuItem value="Todas">Todas</MenuItem>
            {frecuencias.map((f) => (
              <MenuItem key={f} value={f}>
                {f}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            type="number"
            label="Cuotas en atraso (mín.)"
            value={cuotasMin}
            onChange={(e) => setCuotasMin(e.target.value)}
            inputProps={{ min: 0 }}
            sx={{ ...fpInputSx, width: { sm: 180 } }}
          />
          <TextField
            size="small"
            type="number"
            label="Días de atraso (mín.)"
            value={diasMin}
            onChange={(e) => setDiasMin(e.target.value)}
            inputProps={{ min: 0 }}
            sx={{ ...fpInputSx, width: { sm: 170 } }}
          />
          <Button onClick={limpiarFiltros} sx={btn}>
            Limpiar
          </Button>
        </Stack>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ sm: "center" }}
          sx={{ mb: 1.5 }}
        >
          <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
            {visibles.length} préstamos con estos filtros · use la casilla del encabezado para
            marcarlos todos
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
            <b>{seleccion.length}</b> marcados · Vencido <b>RD$ {fmtMoney(totalVencido)}</b>
          </Typography>
          <Button
            variant="contained"
            onClick={enviar}
            disabled={enviando || !seleccion.length || !incautador}
            startIcon={enviando ? <CircularProgress size={16} color="inherit" /> : null}
            sx={btnAzul}
          >
            Enviar al AdmG
          </Button>
        </Stack>

        <Box sx={{ height: 560, bgcolor: FP.color.cardBg, borderRadius: 2.5 }}>
          <FpDataGrid
            rows={visibles}
            columns={columnas}
            getRowId={(r) => r.noprest}
            loading={cargando}
            checkboxSelection
            keepNonExistentRowsSelected
            rowSelectionModel={seleccion}
            onRowSelectionModelChange={(m) => setSeleccion(m.map(String))}
            vacioIcono={AssignmentLateOutlinedIcon}
            vacioTitulo="Sin préstamos para solicitar"
            vacioSubtitulo="Solo salen préstamos activos con cuotas vencidas, sin incauto ni otra solicitud."
            initialState={{ sorting: { sortModel: [{ field: "dias", sort: "desc" }] } }}
          />
        </Box>
      </Box>
    </Box>
  );
}
NuevoLote.propTypes = {
  onExit: PropTypes.func.isRequired,
  onCreado: PropTypes.func.isRequired,
  noprestInicial: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]),
};
NuevoLote.defaultProps = { noprestInicial: null };

/* ---------------------------------------------------------------------- */
/* Detalle del lote                                                        */
/* ---------------------------------------------------------------------- */

function DetalleLote({ loteId, onExit, onCambio }) {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const [lote, setLote] = useState(null);
  const [lineas, setLineas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [cancelar, setCancelar] = useState(null); // {linea} o {lote}
  const [procesando, setProcesando] = useState(false);
  const [registrar, setRegistrar] = useState(null); // noprest

  const cargar = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/solicitudincauto_detalle", { lote: loteId });
      if (ok) {
        setLote(data.lote);
        setLineas(data.lineas || []);
      } else showAlert({ message: data.Error || "No se pudo leer la solicitud", color: "error" });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loteId]);

  const confirmarCancelar = async (razon) => {
    setProcesando(true);
    try {
      const { ok, data } = await post("/api/solicitudincauto_cancelar", { ...cancelar, razon });
      if (ok) {
        showAlert({ message: data.mensajeok || "Solicitud cancelada", color: "success" });
        setCancelar(null);
        cargar();
        onCambio();
      } else showAlert({ message: data.Error || "No se pudo cancelar", color: "error" });
    } finally {
      setProcesando(false);
    }
  };

  // Acta de Incautación por préstamo (datos del expediente, garante, vehículo
  // y foto). Sin `linea`, todas las del lote en un solo trabajo.
  const [imprimiendo, setImprimiendo] = useState(null);
  const imprimirActas = async (linea = null) => {
    setImprimiendo(linea || "todas");
    try {
      const { ok, data } = await post("/api/solicitudincauto_actas", {
        lote: loteId,
        ...(linea ? { linea } : {}),
      });
      if (ok && Array.isArray(data.actas) && data.actas.length) imprimirActasIncauto(data.actas);
      else
        showAlert({ message: data.Error || "No se pudieron preparar las actas", color: "error" });
    } finally {
      setImprimiendo(null);
    }
  };
  const conActa = (l) =>
    !["R", "X"].includes(l.estado) &&
    l.estadotexto !== "Rechazada" &&
    l.estadotexto !== "Cancelada";

  const vivo = lote && ["Pendiente", "Aprobado"].includes(lote.estado);
  const porEjecutar = lineas.filter((l) => l.estadotexto === "Por ejecutar");

  const columnas = [
    { field: "noprest", headerName: "Préstamo", width: 95 },
    { field: "cliente", headerName: "Cliente", flex: 1.2, minWidth: 170 },
    { field: "chasis", headerName: "Chasis", width: 150 },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      flex: 1,
      minWidth: 150,
      valueGetter: (_, r) => [r.marca, r.modelo, r.colore].filter(Boolean).join(" "),
    },
    { field: "dias", headerName: "Días hoy", type: "number", width: 85 },
    {
      field: "vencido",
      headerName: "Vencido hoy",
      type: "number",
      width: 120,
      valueFormatter: (v) => fmtMoney(v),
    },
    {
      field: "estadotexto",
      headerName: "Estado",
      width: 190,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ height: "100%" }}>
          <Tooltip title={row.razon || ""}>
            <span>
              <StatusPill
                label={row.estadotexto}
                variant={PILL_LINEA[row.estadotexto] || "neutral"}
              />
            </span>
          </Tooltip>
          {row.estado === "" && row.aldia ? (
            <Tooltip title="El cliente ya no tiene cuotas vencidas">
              <span>
                <StatusPill label="Al día" variant="success" />
              </span>
            </Tooltip>
          ) : null}
          {row.estado === "" && !row.prestamoactivo ? (
            <Tooltip title="El préstamo ya no está activo">
              <span>
                <StatusPill label="Inactivo" variant="danger" />
              </span>
            </Tooltip>
          ) : null}
        </Stack>
      ),
    },
    {
      field: "acciones",
      headerName: "",
      width: 250,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: "100%" }}>
          {conActa(row) ? (
            <Tooltip title="Imprimir acta de incautación">
              <span>
                <IconButton
                  size="small"
                  disabled={!!imprimiendo}
                  onClick={() => imprimirActas(row.id)}
                >
                  {imprimiendo === row.id ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DescriptionOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          ) : null}
          {row.estadotexto === "Por ejecutar" && row.prestamoactivo ? (
            <Button size="small" onClick={() => setRegistrar(row.noprest)} sx={btn}>
              Registrar incauto
            </Button>
          ) : null}
          {row.estado === "" && vivo ? (
            <Button size="small" onClick={() => setCancelar({ linea: row.id })} sx={btnRojo}>
              Cancelar
            </Button>
          ) : null}
        </Stack>
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <BarraVolver onExit={onExit} />
      <PageHeader
        title={`Solicitud de incauto #${loteId}`}
        subtitle={lote ? `${lote.incautador} · ${lote.motivo}` : ""}
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {lote && (
          <Box
            sx={{
              p: 2.4,
              mb: 2,
              borderRadius: 2.5,
              bgcolor: FP.color.cardBg,
              border: `1px solid ${FP.color.border}`,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              justifyContent="space-between"
              alignItems={{ md: "center" }}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
                  <StatusPill label={lote.estado} variant={PILL_LOTE[lote.estado] || "neutral"} />
                  {lote.vencida ? (
                    <StatusPill label="Fecha límite vencida" variant="danger" />
                  ) : null}
                </Stack>
                <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
                  Solicitado por <b>{lote.user}</b> el {lote.fecha}
                  {lote.fechalimite ? ` · Fecha límite ${lote.fechalimite}` : ""}
                  {lote.aprobadopor ? ` · Resuelto por ${lote.aprobadopor}` : ""}
                </Typography>
                <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
                  {lote.ejecutadas} de {lote.total} ejecutados · {lote.vigentes} pendientes ·{" "}
                  {lote.descartadas} rechazados o cancelados
                </Typography>
                {lote.nota ? (
                  <Typography sx={{ fontSize: "0.85rem", mt: 0.5 }}>{lote.nota}</Typography>
                ) : null}
                {lote.comentarioaut ? (
                  <Typography sx={{ fontSize: "0.85rem", mt: 0.5 }}>
                    <b>AdmG:</b> {lote.comentarioaut}
                  </Typography>
                ) : null}
                {lote.razon ? (
                  <Typography sx={{ fontSize: "0.85rem", mt: 0.5 }}>
                    <b>Cancelado:</b> {lote.razon}
                  </Typography>
                ) : null}
              </Box>
              <Stack direction="row" spacing={1}>
                {lineas.some(conActa) ? (
                  <Button
                    variant="outlined"
                    startIcon={
                      imprimiendo === "todas" ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DescriptionOutlinedIcon />
                      )
                    }
                    disabled={!!imprimiendo}
                    onClick={() => imprimirActas()}
                    sx={btn}
                  >
                    Imprimir actas ({lineas.filter(conActa).length})
                  </Button>
                ) : null}
                {lote.estado === "Aprobado" && porEjecutar.length > 0 ? (
                  <Button
                    variant="contained"
                    startIcon={<PrintOutlinedIcon />}
                    onClick={() => imprimirOrdenLote(lote, porEjecutar)}
                    sx={btnAzul}
                  >
                    Imprimir orden
                  </Button>
                ) : null}
                {vivo ? (
                  <Button
                    variant="outlined"
                    onClick={() => setCancelar({ lote: lote.id })}
                    sx={btnRojo}
                  >
                    Cancelar lote
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Box>
        )}
        <Box sx={{ height: 520, bgcolor: FP.color.cardBg, borderRadius: 2.5 }}>
          <FpDataGrid rows={lineas} columns={columnas} loading={cargando} />
        </Box>
      </Box>

      <RazonDialog
        open={!!cancelar}
        titulo={cancelar?.lote ? "Cancelar todo el lote" : "Cancelar este préstamo del lote"}
        onClose={() => setCancelar(null)}
        onConfirmar={confirmarCancelar}
        procesando={procesando}
      />

      <Dialog fullScreen open={!!registrar} onClose={() => setRegistrar(null)}>
        {registrar ? (
          <RegistrarIncauto
            embedded
            noprestInicial={registrar}
            onExit={() => {
              setRegistrar(null);
              cargar();
              onCambio();
            }}
            onSaved={() => {
              setRegistrar(null);
              cargar();
              onCambio();
            }}
          />
        ) : null}
      </Dialog>
    </Box>
  );
}
DetalleLote.propTypes = {
  loteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onExit: PropTypes.func.isRequired,
  onCambio: PropTypes.func.isRequired,
};

/* ---------------------------------------------------------------------- */
/* Chasis en curso (todos los lotes)                                       */
/* ---------------------------------------------------------------------- */

const FILTROS_CHASIS = [
  { id: "todos", label: "Todos" },
  { id: "ejecutar", label: "Por ejecutar", color: FP.color.warning },
  { id: "pendiente", label: "Por aprobar", color: FP.color.info },
  { id: "vencida", label: "Fecha límite vencida", color: FP.color.danger },
];
const pasaFiltroChasis = (c, f) => {
  if (f === "ejecutar") return c.estadotexto === "Por ejecutar";
  if (f === "pendiente") return c.estadotexto === "Pendiente";
  if (f === "vencida") return c.vencida;
  return true;
};

function ChasisEnCurso({ refreshToken, onAbrirLote, onCambio }) {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState("todos");
  const [incautador, setIncautador] = useState("");
  const [buscar, setBuscar] = useState("");
  const [registrar, setRegistrar] = useState(null);
  const [imprimiendo, setImprimiendo] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const { ok, data } = await post("/api/solicitudincauto_encurso", {});
      if (ok) setFilas(data.chasis || []);
      else showAlert({ message: data.Error || "No se pudieron cargar los chasis", color: "error" });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  const incautadores = useMemo(
    () => Array.from(new Set(filas.map((f) => f.incautador).filter(Boolean))).sort(),
    [filas]
  );

  const visibles = useMemo(() => {
    const q = buscar.trim().toLowerCase();
    return filas.filter((c) => {
      if (!pasaFiltroChasis(c, filtro)) return false;
      if (incautador && c.incautador !== incautador) return false;
      if (!q) return true;
      return [c.chasis, c.noprest, c.cliente, c.cedula, c.lote, c.marca, c.modelo]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [filas, filtro, incautador, buscar]);

  const imprimirActa = async (c) => {
    setImprimiendo(c.id);
    try {
      const { ok, data } = await post("/api/solicitudincauto_actas", { lote: c.lote, linea: c.id });
      if (ok && Array.isArray(data.actas) && data.actas.length) imprimirActasIncauto(data.actas);
      else showAlert({ message: data.Error || "No se pudo preparar el acta", color: "error" });
    } finally {
      setImprimiendo(null);
    }
  };

  const exportar = () => {
    if (!visibles.length) return;
    const hoja = XLSX.utils.aoa_to_sheet([
      [
        "Chasis",
        "Préstamo",
        "Cliente",
        "Cédula",
        "Vehículo",
        "Lote",
        "Incautador",
        "Estado",
        "Días de atraso",
        "Vencido",
        "Fecha límite",
        "Sucursal",
      ],
      ...visibles.map((c) => [
        c.chasis,
        c.noprest,
        c.cliente,
        c.cedula,
        [c.marca, c.modelo, c.colore].filter(Boolean).join(" "),
        c.lote,
        c.incautador,
        c.estadotexto,
        c.dias,
        c.vencido,
        c.fechalimite || "",
        c.sucursal,
      ]),
    ]);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Chasis en curso");
    XLSX.writeFile(libro, `ChasisEnCurso_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const columnas = [
    { field: "chasis", headerName: "Chasis", width: 160 },
    { field: "noprest", headerName: "Préstamo", width: 90 },
    { field: "cliente", headerName: "Cliente", flex: 1.1, minWidth: 160 },
    {
      field: "vehiculo",
      headerName: "Vehículo",
      flex: 0.9,
      minWidth: 140,
      valueGetter: (_, r) => [r.marca, r.modelo, r.colore].filter(Boolean).join(" "),
    },
    { field: "lote", headerName: "Lote", width: 70 },
    { field: "incautador", headerName: "Incautador", flex: 0.9, minWidth: 140 },
    {
      field: "estadotexto",
      headerName: "Estado",
      width: 190,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.6} alignItems="center" sx={{ height: "100%" }}>
          <StatusPill label={row.estadotexto} variant={PILL_LINEA[row.estadotexto] || "neutral"} />
          {row.aldia ? (
            <Tooltip title="El cliente ya no tiene cuotas vencidas">
              <span>
                <StatusPill label="Al día" variant="success" />
              </span>
            </Tooltip>
          ) : null}
          {!row.prestamoactivo ? (
            <Tooltip title="El préstamo ya no está activo">
              <span>
                <StatusPill label="Inactivo" variant="danger" />
              </span>
            </Tooltip>
          ) : null}
        </Stack>
      ),
    },
    { field: "dias", headerName: "Días", type: "number", width: 70 },
    {
      field: "vencido",
      headerName: "Vencido",
      type: "number",
      width: 110,
      valueFormatter: (v) => fmtMoney(v),
    },
    {
      field: "fechalimite",
      headerName: "Fecha límite",
      width: 110,
      renderCell: ({ row }) => (
        <span
          style={{
            color: row.vencida ? FP.color.danger : undefined,
            fontWeight: row.vencida ? 700 : 400,
          }}
        >
          {row.fechalimite || "—"}
        </span>
      ),
    },
    { field: "sucursal", headerName: "Suc.", width: 60 },
    {
      field: "acciones",
      headerName: "",
      width: 230,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.3} alignItems="center" sx={{ height: "100%" }}>
          {row.estadotexto === "Por ejecutar" && row.prestamoactivo ? (
            <Button size="small" onClick={() => setRegistrar(row.noprest)} sx={btn}>
              Registrar incauto
            </Button>
          ) : null}
          <Tooltip title="Imprimir acta de incautación">
            <span>
              <IconButton size="small" disabled={!!imprimiendo} onClick={() => imprimirActa(row)}>
                {imprimiendo === row.id ? (
                  <CircularProgress size={16} />
                ) : (
                  <DescriptionOutlinedIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={`Abrir la solicitud #${row.lote}`}>
            <IconButton size="small" onClick={() => onAbrirLote(row.lote)}>
              <OpenInNewRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "center" }}
        sx={{ mb: 2 }}
      >
        <FiltroPildoras
          valor={filtro}
          onChange={setFiltro}
          opciones={FILTROS_CHASIS.map((f) => ({
            id: f.id,
            label: f.label,
            color: f.color,
            conteo: filas.filter((c) => pasaFiltroChasis(c, f.id)).length,
          }))}
          sx={{ flex: 1 }}
        />
        <TextField
          select
          size="small"
          label="Incautador"
          value={incautador}
          onChange={(e) => setIncautador(e.target.value)}
          sx={{ ...fpInputSx, minWidth: { md: 190 } }}
        >
          <MenuItem value="">Todos</MenuItem>
          {incautadores.map((i) => (
            <MenuItem key={i} value={i}>
              {i}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          placeholder="Buscar chasis, préstamo, cliente…"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          sx={{ ...fpInputSx, minWidth: { md: 240 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button onClick={cargar} startIcon={<RefreshRoundedIcon />} sx={btn}>
          Actualizar
        </Button>
        <Button
          onClick={exportar}
          disabled={!visibles.length}
          startIcon={<FileDownloadOutlinedIcon />}
          sx={btn}
        >
          Excel
        </Button>
      </Stack>
      <Box sx={{ height: 600, bgcolor: FP.color.cardBg, borderRadius: 2.5 }}>
        <FpDataGrid
          rows={visibles}
          columns={columnas}
          loading={cargando}
          vacioIcono={AssignmentLateOutlinedIcon}
          vacioTitulo="Sin chasis en curso"
          vacioSubtitulo="Aquí salen los préstamos pendientes de todas las solicitudes por aprobar o aprobadas."
          initialState={{ sorting: { sortModel: [{ field: "dias", sort: "desc" }] } }}
        />
      </Box>

      <Dialog fullScreen open={!!registrar} onClose={() => setRegistrar(null)}>
        {registrar ? (
          <RegistrarIncauto
            embedded
            noprestInicial={registrar}
            onExit={() => {
              setRegistrar(null);
              cargar();
              onCambio();
            }}
            onSaved={() => {
              setRegistrar(null);
              cargar();
              onCambio();
            }}
          />
        ) : null}
      </Dialog>
    </>
  );
}
ChasisEnCurso.propTypes = {
  refreshToken: PropTypes.number.isRequired,
  onAbrirLote: PropTypes.func.isRequired,
  onCambio: PropTypes.func.isRequired,
};

/* ---------------------------------------------------------------------- */
/* Lista de lotes                                                          */
/* ---------------------------------------------------------------------- */

export default function SolicitudesIncauto({ embedded, onExit, noprestInicial }) {
  const { post } = useApi();
  const { showAlert } = useGlobalMDAlert();
  const location = useLocation();
  const navigate = useNavigate();
  // Morosidad y Gestión de cobros llegan aquí con los préstamos en el state
  // de la navegación (no en la URL: una categoría puede traer cientos).
  const [pedidos] = useState(() => {
    const desdeProp = normalizarNoprests(noprestInicial);
    return desdeProp.length ? desdeProp : normalizarNoprests(location.state?.noprests);
  });
  const [lotes, setLotes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtro, setFiltro] = useState("activas");
  const [buscar, setBuscar] = useState("");
  const [nuevo, setNuevo] = useState(pedidos.length > 0);

  // Que recargar la página no vuelva a abrir el formulario con esos préstamos.
  useEffect(() => {
    if (!embedded && location.state?.noprests)
      navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [abierto, setAbierto] = useState(null);
  // Vista: lotes (una fila por solicitud) o chasis en curso de todos los lotes.
  const [vista, setVista] = useState("lotes");
  const [refrescoChasis, setRefrescoChasis] = useState(0);

  const cargar = async () => {
    setCargando(true);
    setRefrescoChasis((n) => n + 1);
    try {
      const { ok, data } = await post("/api/solicitudincauto_listar", {});
      if (ok) setLotes(data.lotes || []);
      else
        showAlert({
          message: data.Error || "No se pudieron cargar las solicitudes",
          color: "error",
        });
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibles = useMemo(() => {
    const f = FILTROS.find((x) => x.id === filtro);
    const q = buscar.trim().toLowerCase();
    return lotes.filter((l) => {
      if (f && f.estados && !f.estados.includes(l.estado)) return false;
      if (!q) return true;
      return [l.id, l.incautador, l.cedincaut, l.motivo, l.user, l.sucursal]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [lotes, filtro, buscar]);

  const columnas = [
    { field: "id", headerName: "Lote", width: 75 },
    { field: "fecha", headerName: "Fecha", width: 100 },
    { field: "incautador", headerName: "Incautador", flex: 1.2, minWidth: 170 },
    { field: "motivo", headerName: "Motivo", flex: 0.8, minWidth: 130 },
    {
      field: "avance",
      headerName: "Avance",
      width: 120,
      valueGetter: (_, r) => `${r.ejecutadas} de ${r.total - r.descartadas}`,
    },
    {
      field: "fechalimite",
      headerName: "Fecha límite",
      width: 115,
      renderCell: ({ row }) => (
        <span
          style={{
            color: row.vencida ? FP.color.danger : undefined,
            fontWeight: row.vencida ? 700 : 400,
          }}
        >
          {row.fechalimite || "—"}
        </span>
      ),
    },
    {
      field: "estado",
      headerName: "Estado",
      width: 120,
      renderCell: ({ row }) => (
        <StatusPill label={row.estado} variant={PILL_LOTE[row.estado] || "neutral"} />
      ),
    },
    { field: "user", headerName: "Solicitado por", width: 130 },
    { field: "sucursal", headerName: "Suc.", width: 70 },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      {embedded && onExit ? <BarraVolver onExit={onExit} /> : null}
      <PageHeader
        title="Solicitudes de Incauto"
        subtitle="Lotes de préstamos a incautar por incautador, con una sola aprobación del AdmG"
        cta={
          <Stack direction="row" spacing={1.2} alignItems="center">
            <ToggleButtonGroup
              exclusive
              size="small"
              value={vista}
              onChange={(_, v) => v && setVista(v)}
              sx={{
                bgcolor: FP.color.cardBg,
                borderRadius: 2,
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  px: 1.8,
                  border: `1px solid ${FP.color.border}`,
                  color: FP.color.textSecondary,
                },
                "& .Mui-selected": {
                  bgcolor: `${FP.color.navy} !important`,
                  color: "#FFFFFF !important",
                },
              }}
            >
              <ToggleButton value="lotes">Solicitudes</ToggleButton>
              <ToggleButton value="chasis">Chasis en curso</ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNuevo(true)}
              sx={btnAzul}
            >
              Nueva solicitud
            </Button>
          </Stack>
        }
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {vista === "chasis" ? (
          <ChasisEnCurso
            refreshToken={refrescoChasis}
            onAbrirLote={(id) => setAbierto(id)}
            onCambio={cargar}
          />
        ) : (
          <>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              alignItems={{ md: "center" }}
              sx={{ mb: 2 }}
            >
              <FiltroPildoras
                valor={filtro}
                onChange={setFiltro}
                opciones={FILTROS.map((f) => ({
                  id: f.id,
                  label: f.label,
                  color: f.color,
                  conteo: f.estados
                    ? lotes.filter((l) => f.estados.includes(l.estado)).length
                    : lotes.length,
                }))}
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                placeholder="Buscar lote, incautador, motivo…"
                value={buscar}
                onChange={(e) => setBuscar(e.target.value)}
                sx={{ ...fpInputSx, minWidth: { md: 260 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button onClick={cargar} startIcon={<RefreshRoundedIcon />} sx={btn}>
                Actualizar
              </Button>
            </Stack>
            <Box sx={{ height: 600, bgcolor: FP.color.cardBg, borderRadius: 2.5 }}>
              <FpDataGrid
                rows={visibles}
                columns={columnas}
                loading={cargando}
                onRowClick={({ row }) => setAbierto(row.id)}
                sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
                vacioIcono={AssignmentLateOutlinedIcon}
                vacioTitulo="Sin solicitudes"
                vacioSubtitulo='Cree una con "Nueva solicitud".'
              />
            </Box>
          </>
        )}
      </Box>

      <Dialog fullScreen open={nuevo} onClose={() => setNuevo(false)}>
        {nuevo ? (
          <NuevoLote
            noprestInicial={pedidos}
            onExit={() => setNuevo(false)}
            onCreado={() => {
              setNuevo(false);
              setFiltro("activas");
              cargar();
            }}
          />
        ) : null}
      </Dialog>

      <Dialog fullScreen open={!!abierto} onClose={() => setAbierto(null)}>
        {abierto ? (
          <DetalleLote loteId={abierto} onExit={() => setAbierto(null)} onCambio={cargar} />
        ) : null}
      </Dialog>
    </Box>
  );
}
SolicitudesIncauto.propTypes = {
  embedded: PropTypes.bool,
  onExit: PropTypes.func,
  noprestInicial: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]),
};
SolicitudesIncauto.defaultProps = { embedded: false, onExit: null, noprestInicial: null };
